import { ReactElement, useEffect, useMemo, useState } from 'react';
import { Button, Stack, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router';
import { useSnackbar } from 'notistack';
import { useAuthContext } from 'src/auth/useAuthContext';
import LoadingScreen from 'src/components/loading-screen';
import AuthFlowLayout from 'src/layouts/AuthFlow/AuthFlowLayout';
import { translateKey } from 'src/locales/translate';
import { PATH_AUTH } from 'src/routes/paths';
import { getFirebaseErrorMessage } from 'src/utils/getFirebaseErrorMessage';

// ----------------------------------------------------------------------

export default function Verification(): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const navigtate = useNavigate();
  const { search } = useLocation();
  const { verifyEmail } = useAuthContext();
  const [state, setState] = useState({
    isLoading: true,
    error: false,
    success: false,
  });

  const query = useMemo(() => new URLSearchParams(search), [search]);
  const mode = query.get('mode');
  const actionCode = query.get('oobCode');
  const loginPath = query
    .get('continueUrl')
    ?.endsWith(PATH_AUTH.professionalLogin)
    ? PATH_AUTH.professionalLogin
    : PATH_AUTH.login;

  useEffect(() => {
    const verify = async (): Promise<void> => {
      if (!actionCode || mode !== 'verifyEmail') {
        setState(() => ({ success: false, isLoading: false, error: true }));
      }

      if (actionCode && mode === 'verifyEmail' && verifyEmail) {
        try {
          setState((prevState) => ({ ...prevState, isLoading: true }));
          await verifyEmail(actionCode);
          setState((prevState) => ({
            ...prevState,
            success: true,
            isLoading: false,
          }));
        } catch (error) {
          setState(() => ({ success: false, isLoading: false, error: true }));
          enqueueSnackbar(getFirebaseErrorMessage(error.code) || error, {
            variant: 'error',
          });
        }
      }
    };

    verify();
  }, [actionCode, mode, verifyEmail]);

  const onButtonClick = (): void => navigtate(loginPath);

  if (state.isLoading) {
    return <LoadingScreen />;
  }

  return (
    <AuthFlowLayout screen={state.error ? 'verificationError' : 'verified'}>
      <Stack spacing={3}>
        <Typography variant="body2" color="text.secondary">
          {state.error
            ? translateKey(
                'hardcoded.nie_moglismy_zweryfikowac_twojego_adresu_e_mail_sprobuj',
              )
            : translateKey(
                'hardcoded.twoj_adres_zostal_zweryfikowany_teraz_gdy_jestes_czescia',
              )}
        </Typography>
        <Button variant="contained" size="large" onClick={onButtonClick}>
          {translateKey('hardcoded.powrot_do_strony_logowania')}
        </Button>
      </Stack>
    </AuthFlowLayout>
  );
}
